import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setTeams,
  setTotal,
  setPage,
  setSize,
  setSearch,
  setFilterType,
  setShowForm,
  setFormData,
  setSelectedUsers,
  loadEditingTeam,
  resetForm,
} from '../../../store/teamSlice';
import { TopNav, SideNav } from '../navigation/ConsoleNavigation';
import axios from 'axios';
import TeamForm from './TeamForm';
import TeamList from './TeamList';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const TeamManagement = () => {
  const dispatch = useDispatch();

  // ╔══ Redux-persisted state (survives page navigation) ══╗
  const {
    teams,
    total,
    page,
    size,
    search,
    filterType,
    showForm,
    editingTeam,
    formData,
    selectedUsers,
  } = useSelector((state) => state.team);

  // ╔══ Local transient state (resets on mount — intentional) ══╗
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const navigate = useNavigate();

  // API base URL
  const API_BASE_URL = '/pceapi/teams';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Fetch teams on mount and when dependencies change
  useEffect(() => {
    fetchTeams();
  }, [page, size, search, refreshTrigger, filterType]);

  // Fetch teams with pagination and search
  const fetchTeams = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading teams');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/list`,
        {
          page,
          size,
          search: search || undefined,
          filter_type: filterType
        },
        { headers }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load teams');
        setIsErrorPopupVisible(true);
        return;
      }

      dispatch(setTeams(data.data || []));
      dispatch(setTotal(data.total || 0));
    } catch (error) {
      console.error('Error fetching teams:', error);
      setPopupErrorMessage(error.message || 'Failed to load teams');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setPopupErrorMessage('Team name is required');
      setIsErrorPopupVisible(true);
      return;
    }

    setIsProcessingPopupVisible(true);
    setProcessingMessage(editingTeam ? 'Updating team' : 'Creating team');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('team_access_level', formData.team_access_level);

      if (selectedUsers.length > 0) {
        formDataToSend.append('user_access', JSON.stringify(selectedUsers));
      }

      let response;

      if (editingTeam) {
        formDataToSend.append('team_id', editingTeam.id);
        response = await axios.post(
          `${API_BASE_URL}/update`,
          formDataToSend,
          { headers }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/create`,
          formDataToSend,
          { headers }
        );
      }

      const result = response.data;

      if (result.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (result.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message || (editingTeam ? 'Failed to update team' : 'Failed to create team'));
        setIsErrorPopupVisible(true);
        return;
      }

      setPopupSuccessMessage(result.message || (editingTeam ? 'Team updated successfully' : 'Team created successfully'));
      setIsSuccessPopupVisible(true);
      setRefreshTrigger(prev => prev + 1);
      dispatch(resetForm());
    } catch (error) {
      console.error('Submit error:', error);
      setPopupErrorMessage(error.message || (editingTeam ? 'Failed to update team' : 'Failed to create team'));
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Open form for editing a team (bulk action)
  const handleOpenEditForm = (team) => {
    dispatch(loadEditingTeam(team));
  };

  // Wrapper setters that dispatch to Redux (passed as props to children)
  const handleSetPage = (val) => dispatch(setPage(val));
  const handleSetSize = (val) => dispatch(setSize(val));
  const handleSetSearch = (val) => dispatch(setSearch(val));
  const handleSetFilterType = (val) => dispatch(setFilterType(val));
  const handleSetFormData = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setFormData(updater(formData)));
    } else {
      dispatch(setFormData(updater));
    }
  };
  const handleSetSelectedUsers = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setSelectedUsers(updater(selectedUsers)));
    } else {
      dispatch(setSelectedUsers(updater));
    }
  };

  // Popup close handlers
  const closeErrorPopup = () => setIsErrorPopupVisible(false);
  const closeSuccessPopup = () => setIsSuccessPopupVisible(false);

  return (
    <div className="main-window">
      <TopNav />
      <div className="container-fluid">
        <div className="row">
          <SideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="mx-5 pb-5">
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-0 margin-top-4">
                <p className="ev-med-p-blue mb-1">Project Teams <span className="ev-light-reg">({total})</span></p>
                {!showForm && (
                  <button
                    className="ev-btn-yellow-fill-round ev-btn-med ev-bold-reg"
                    onClick={() => {
                      dispatch(resetForm());
                      dispatch(setShowForm(true));
                    }}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Create Team
                  </button>
                )}
              </div>
              <p className="ev-message-card2 mb-4">Manage project teams and team memberships for collaborative access to platform resources.</p>

              {/* Show form or team list based on state */}
              {showForm ? (
                <TeamForm
                  editingTeam={editingTeam}
                  formData={formData}
                  setFormData={handleSetFormData}
                  selectedUsers={selectedUsers}
                  setSelectedUsers={handleSetSelectedUsers}
                  onSubmit={handleSubmit}
                  onCancel={() => dispatch(resetForm())}
                />
              ) : (
                <TeamList
                  teams={teams}
                  total={total}
                  page={page}
                  setPage={handleSetPage}
                  size={size}
                  setSize={handleSetSize}
                  search={search}
                  setSearch={handleSetSearch}
                  filterType={filterType}
                  setFilterType={handleSetFilterType}
                  onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                  onEditTeam={handleOpenEditForm}
                />
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}

      {isErrorPopupVisible && (
        <ErrorPopup
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}

      {isSuccessPopupVisible && (
        <SuccessPopup
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
    </div>
  );
};

export default TeamManagement;